// client/src/pages/AdminExamQuestions.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../api";
import { 
  ArrowLeft, 
  Plus, 
  Save, 
  Trash2, 
  GripVertical, 
  CheckCircle, 
  Type, 
  ListOrdered, 
  MoreHorizontal,
  CornerDownRight,
  AlertCircle
} from "lucide-react";

function AdminExamQuestions() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    text: "",
    type: "mcq",
    optionA: "",
    optionB: "",
    optionC: "",
    optionD: "",
    correctOptionKey: "A",
    marks: 1,
    order: 0,
  });

  const admin = JSON.parse(localStorage.getItem("user") || "{}");

  // --- LOGIC (PRESERVED) ---
  useEffect(() => {
    if (!admin || admin.role !== "admin") {
      navigate("/");
    }
  }, [admin, navigate]);

  useEffect(() => {
    const fetchExamAndQuestions = async () => {
      try {
        const examRes = await api.get(`/api/exams/${examId}`);
        setExam(examRes.data || null);

        const qRes = await api.get(`/api/admin/exams/${examId}/questions`);
        // Sort questions by order for display
        const sortedQs = (qRes.data || []).sort((a, b) => a.order - b.order);
        setQuestions(sortedQs);
      } catch (err) {
        console.error("Fetch exam/questions error:", err.response?.data || err.message);
      }
    };

    if (examId) fetchExamAndQuestions();
  }, [examId]);

  const resetForm = () => {
    setForm({
      text: "",
      type: "mcq",
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      correctOptionKey: "A",
      marks: 1,
      order: questions.length + 1, // Auto-increment order suggestion
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!editingId) {
        // create
        const res = await api.post(`/api/admin/exams/${examId}/questions`, form);
        setQuestions((prev) => [...prev, res.data].sort((a, b) => a.order - b.order));
      } else {
        // update
        const res = await api.put(`/api/admin/questions/${editingId}`, form);
        setQuestions((prev) =>
          prev.map((q) => (q._id === editingId ? res.data : q)).sort((a, b) => a.order - b.order)
        );
      }
      resetForm();
    } catch (err) {
      console.error("Save question error:", err.response?.data || err.message);
      alert("Failed to save question");
    }
  };

  const handleEdit = (q) => {
    setEditingId(q._id);
    setForm({
      text: q.text,
      type: q.type,
      optionA: q.options.find((o) => o.key === "A")?.text || "",
      optionB: q.options.find((o) => o.key === "B")?.text || "",
      optionC: q.options.find((o) => o.key === "C")?.text || "",
      optionD: q.options.find((o) => o.key === "D")?.text || "",
      correctOptionKey: q.correctOptionKey || "A",
      marks: q.marks,
      order: q.order,
    });
  };

  const handleDelete = async (idToDelete) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await api.delete(`/api/admin/questions/${idToDelete}`);
      setQuestions((prev) => prev.filter((q) => q._id !== idToDelete));
      if (editingId === idToDelete) resetForm();
    } catch (err) {
      console.error("Delete question error:", err.response?.data || err.message);
      alert("Failed to delete question");
    }
  };

  // --- UI RENDER ---

  if (!exam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        Loading editor...
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 font-sans overflow-hidden">
      
      {/* 1. Header Navigation */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0 z-20">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/admin")}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-800 leading-tight">Exam Editor</h1>
            <p className="text-xs text-gray-500">{exam.title} • {questions.length} Questions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
            <button 
                onClick={resetForm}
                className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 font-medium text-sm transition-colors"
            >
                <Plus size={16} /> New Question
            </button>
        </div>
      </header>

      {/* 2. Main Content Area (Split View) */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* LEFT PANEL: Question List */}
        <aside className="w-1/3 min-w-[320px] max-w-[400px] bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
          <div className="p-4 bg-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase tracking-wider sticky top-0 z-10 backdrop-blur-sm">
             Question Index
          </div>
          
          <div className="flex-1">
            {questions.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                    <ListOrdered size={32} className="mx-auto mb-2 opacity-50"/>
                    <p>No questions yet.</p>
                </div>
            ) : (
                <ul>
                    {questions.map((q) => (
                        <li 
                            key={q._id} 
                            onClick={() => handleEdit(q)}
                            className={`group border-b border-gray-100 p-4 cursor-pointer hover:bg-gray-50 transition-all relative
                                ${editingId === q._id ? 'bg-orange-50/50 border-l-4 border-l-orange-500' : 'border-l-4 border-l-transparent'}
                            `}
                        >
                            <div className="flex items-start gap-3">
                                {/* Drag Handle Visual */}
                                <div className="mt-1 text-gray-300 group-hover:text-gray-500 cursor-move">
                                    <GripVertical size={16} />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="text-xs font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Q{q.order}</span>
                                        <span className="text-xs font-semibold text-gray-400">{q.marks} Marks</span>
                                    </div>
                                    <p className="text-sm text-gray-800 font-medium line-clamp-2 leading-snug">
                                        {q.text}
                                    </p>
                                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                                        <span className="capitalize bg-gray-50 px-1.5 rounded border border-gray-200">{q.type}</span>
                                        <span>•</span>
                                        <span className="text-green-600 font-bold">Ans: {q.correctOptionKey}</span>
                                    </div>
                                </div>
                                
                                {/* Delete Action (Visible on Hover) */}
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleDelete(q._id); }}
                                    className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
          </div>
        </aside>

        {/* RIGHT PANEL: Editor Form */}
        <main className="flex-1 bg-gray-50 p-6 md:p-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
             
             {/* Editor Card */}
             <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Card Header */}
                <div className="px-6 py-4 border-b border-gray-100 bg-white flex justify-between items-center sticky top-0 z-10">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        {editingId ? "Edit Question" : "New Question"}
                        {editingId && <span className="text-xs font-normal text-white bg-orange-500 px-2 py-0.5 rounded-full">Editing Mode</span>}
                    </h2>
                    <div className="flex items-center gap-2">
                        {editingId && (
                            <button onClick={resetForm} className="text-xs font-medium text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100">
                                Cancel
                            </button>
                        )}
                        <button 
                            onClick={handleSubmit} 
                            className="flex items-center gap-2 bg-gray-900 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors shadow-lg shadow-gray-200"
                        >
                            <Save size={16} /> Save Question
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    
                    {/* Top Row: Meta Data */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-1">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Type</label>
                            <select name="type" value={form.type} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none">
                                <option value="mcq">Multiple Choice</option>
                                <option value="scenario">Scenario</option>
                            </select>
                        </div>
                        <div className="col-span-1">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Marks</label>
                            <input type="number" name="marks" value={form.marks} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"/>
                        </div>
                        <div className="col-span-1">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Order</label>
                            <input type="number" name="order" value={form.order} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"/>
                        </div>
                    </div>

                    {/* Question Text */}
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-1 flex items-center gap-1"><Type size={12}/> Question Text</label>
                        <textarea 
                            name="text" 
                            value={form.text} 
                            onChange={handleChange} 
                            rows={3} 
                            required
                            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:ring-2 focus:ring-orange-500 outline-none resize-y text-base"
                            placeholder="Type the question here..."
                        />
                    </div>

                    <div className="border-t border-gray-100 my-4"></div>

                    {/* Options Grid */}
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-3 flex items-center gap-1"><ListOrdered size={12}/> Answer Options</label>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['A', 'B', 'C', 'D'].map((optKey) => (
                                <div key={optKey} className={`relative p-1 rounded-xl border-2 transition-all ${form.correctOptionKey === optKey ? 'border-green-500 bg-green-50' : 'border-transparent bg-gray-50'}`}>
                                    <div className="flex items-center gap-2 mb-1 px-2">
                                        <span className={`text-xs font-bold ${form.correctOptionKey === optKey ? 'text-green-700' : 'text-gray-500'}`}>Option {optKey}</span>
                                        {form.correctOptionKey === optKey && <CheckCircle size={12} className="text-green-600"/>}
                                    </div>
                                    <input 
                                        name={`option${optKey}`}
                                        value={form[`option${optKey}`]}
                                        onChange={handleChange}
                                        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                                        placeholder={`Answer for option ${optKey}`}
                                    />
                                    
                                    {/* Quick Select Correct Answer Button */}
                                    <button
                                        type="button"
                                        onClick={() => setForm(prev => ({ ...prev, correctOptionKey: optKey }))}
                                        className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded cursor-pointer transition-colors
                                            ${form.correctOptionKey === optKey ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}
                                        `}
                                    >
                                        {form.correctOptionKey === optKey ? 'Correct Answer' : 'Set Correct'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
             </div>
             
             {/* Bottom Hint */}
             <div className="mt-4 flex items-start gap-2 text-xs text-gray-400 px-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                <p>Ensure the "Order" number is correct to maintain the sequence in the exam. Drag support is visual only; use the input to change order.</p>
             </div>

          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminExamQuestions;